/**
 * O recorte em que um vínculo pode ser gravado.
 *
 * O servidor só aceita vínculo apontando para a entidade do contexto ativo (#408). Fora da
 * plataforma existe uma entidade possível e só uma, então a tela a mostra em vez de perguntá-la.
 */

import { useMemo } from 'react';

import { useManifesto } from '../stores/manifestoStore';
import type { Perspectiva } from '../types/manifesto';
import { BindingEntityType } from '../types/user';

const TIPO_POR_PERSPECTIVA: Partial<Record<Perspectiva, BindingEntityType>> = {
  empresa: BindingEntityType.COMPANY,
  prestador: BindingEntityType.PROVIDER,
};

export interface EntidadeDoVinculo {
  entityType: BindingEntityType;
  entityId: number;
  /** Vazio quando o manifesto não nomeou o contexto ativo: a tela usa um rótulo genérico. */
  nome: string;
  detalhe?: string;
}

export interface BindingScope {
  /** Ausente enquanto o manifesto não chegou. */
  perspectiva?: Perspectiva;
  /** A única entidade aceita. Ausente na plataforma, onde a escolha é real. */
  entidadeFixa?: EntidadeDoVinculo;
  /** Sem outro contexto, mandar trocar de contexto seria instrução impossível. */
  podeTrocarDeContexto: boolean;
}

export function useBindingScope(): BindingScope {
  const { manifesto } = useManifesto();

  return useMemo<BindingScope>(() => {
    if (!manifesto) {
      return { podeTrocarDeContexto: false };
    }

    const { contextoAtivo, contextos } = manifesto;
    const podeTrocarDeContexto = contextos.length > 1;
    const entityType = TIPO_POR_PERSPECTIVA[contextoAtivo.tipo];

    if (!entityType || contextoAtivo.id === undefined) {
      return { perspectiva: contextoAtivo.tipo, podeTrocarDeContexto };
    }

    const escolhido = contextos.find(
      (contexto) => contexto.tipo === contextoAtivo.tipo && contexto.id === contextoAtivo.id
    );

    return {
      perspectiva: contextoAtivo.tipo,
      entidadeFixa: {
        entityType,
        entityId: contextoAtivo.id,
        nome: escolhido?.nome ?? '',
        detalhe: escolhido?.detalhe,
      },
      podeTrocarDeContexto,
    };
  }, [manifesto]);
}

export default useBindingScope;
